import { designMode } from '@/env';
import { api } from '@/api';
import { IBasic, IProduct, IFinance, ISetUpProduct, ISetUpFinance } from './index';
import { toBoolean } from '@/utils';
import { getStoreAccessors } from 'typesafe-vuex';
import { ActionContext } from 'vuex';
import { dispatchCheckApiError } from '../main/actions';
import { State } from '../state';
import { readUserProfile } from '../main/getters';
import { commitAddNotification, commitSetSetupCompleted } from '../main/mutations';
import { MainState } from '../main/state';
import { SetupState } from './state';

type MainContext = ActionContext<MainState, State>;
type SetupContext = ActionContext<SetupState, State>;

export const actions = {
    actionRestoreSetup(context: MainContext) {
        let userProfile = readUserProfile(context)
        let message = (userProfile && (userProfile.first_name && userProfile.last_name)) ? userProfile.last_name + ' ' + userProfile.first_name + 'さん' : ''
        commitAddNotification(context, { content: 'おかえりなさい！' + message, color: 'success' })
    },
    async actionGetBasic(context: SetupContext): Promise<IBasic | null> {
        let data: IBasic | null = null;
        try {
            if (toBoolean(designMode)) {
                data = {
                    company_url: '',
                    company_name: '',
                    company_foundation_date: '',
                    company_industry_category1: '',
                    company_industry_detail1: '',
                    company_industry_category2: '',
                    company_industry_detail2: '',
                    company_industry_category3: '',
                    company_industry_detail3: '',
                    company_growth_phase: '',
                    user_last_name: '',
                    user_first_name: '',
                    user_title: '',
                    user_expectation: [],
                };
            } else {
                const response = await api.getBasic(context.rootState.main.token);
                data = response.data;
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
        return data;
    },
    async actionSetUpBasic(context: SetupContext, payload: IBasic) {
        if (!toBoolean(designMode)) {
            try {
                await Promise.all([
                    api.setupBasic(context.rootState.main.token, payload),
                    await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
                ]);
            } catch (error) {
                await dispatchCheckApiError(context, error);
            }
        }
    },
    async actionGetProduct(context: SetupContext): Promise<IProduct | null> {
        let data: IProduct | null = null;
        try {
            if (toBoolean(designMode)) {
                data = {
                    product_name: '',
                    product_url: '',
                    product_description: '',
                    product_materials: [{
                        id: 1,
                        file_size: 100,
                        file_name: '株式会社会社CFO.Ai事業説明資料.pptx',
                        mime_type: 'application/vnd.ms-powerpoint',
                        created_at: '',
                        created_by: { id: 0 },
                        updated_at: '',
                        updated_by: { id: 0 },
                    }],
                }
            } else {
                const response = await api.getProduct(context.rootState.main.token);
                data = response.data;
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
        return data;
    },
    async actionSetUpProduct(context: SetupContext, payload: ISetUpProduct) {
        try {
            if (!toBoolean(designMode)) {
                if (payload.delete_file_ids.length > 0) {
                    for (let file_id of payload.delete_file_ids) {
                        await Promise.all([
                            api.deleteProductMaterials(context.rootState.main.token, file_id),
                            await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
                        ]);
                    }
                }
                if (payload.upload_files.length > 0) {
                    await Promise.all([
                        api.updateProductMaterials(context.rootState.main.token, payload.upload_files),
                        await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
                    ]);
                }
                await Promise.all([
                    api.setupProduct(context.rootState.main.token, payload.product),
                    await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
                ]);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionGetFinance(context: SetupContext): Promise<IFinance | null> {
        let data: IFinance | null = null;
        try {
            if (toBoolean(designMode)) {
                data = {
                    finance_expect_finance_date: '',
                    finance_expect_finance_amount: 0,
                    finance_expect_valuation_pre: 0,
                    finance_expect_finance_method: [],
                    finance_materials: [],
                    finance_achievement: '',
                    finance_use_of_funds: [],
                    user_need_consultation: null,
                }
            } else {
                const response = await api.getFinance(context.rootState.main.token);
                data = response.data;
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
        return data;
    },
    async actionSetUpFinance(context: SetupContext, payload: ISetUpFinance) {
        try {
            if (!toBoolean(designMode)) {
                if (payload.delete_file_ids.length > 0) {
                    for (let file_id of payload.delete_file_ids) {
                        await Promise.all([
                            api.deleteFinanceMaterials(context.rootState.main.token, file_id),
                            await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
                        ]);
                    }
                }
                if (payload.upload_files.length > 0) {
                    await Promise.all([
                        api.updateFinanceMaterials(context.rootState.main.token, payload.upload_files),
                        await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
                    ]);
                }
                await Promise.all([
                    api.setupFinance(context.rootState.main.token, payload.finance),
                    await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
                ]);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }

    },
    async actionSetupComplete(context: SetupContext) {
        try {
            if (!toBoolean(designMode)) {
                await Promise.all([
                    api.setupComplete(context.rootState.main.token),
                    await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
                ]);
                commitSetSetupCompleted(context, true);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
};

const { dispatch } = getStoreAccessors<SetupState | any, State>('');

export const dispatchRestoreSetup = dispatch(actions.actionRestoreSetup);
export const dispatchGetBasic = dispatch(actions.actionGetBasic);
export const dispatchSetUpBasic = dispatch(actions.actionSetUpBasic);
export const dispatchGetProduct = dispatch(actions.actionGetProduct);
export const dispatchSetUpProduct = dispatch(actions.actionSetUpProduct);
export const dispatchGetFinance = dispatch(actions.actionGetFinance);
export const dispatchSetUpFinance = dispatch(actions.actionSetUpFinance);
export const dispatchSetupComplete = dispatch(actions.actionSetupComplete);